import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { InfoCard } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';

const GRAYLOG_URL = 'https://glog.livedigital.space';
const QUERY_ANNOTATION = 'graylog/query';
const STREAMS_ANNOTATION = 'graylog/streams';

export const isGraylogAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[QUERY_ANNOTATION]);

/** Ссылка на поиск Graylog по запросу и стримам сущности за последние `seconds` секунд. */
export function graylogSearchUrl(
  query: string,
  streams: string | undefined,
  seconds: number,
) {
  const params = new URLSearchParams({
    q: query,
    rangetype: 'relative',
    from: String(seconds),
    relative: String(seconds),
  });
  if (streams) params.set('streams', streams);
  return `${GRAYLOG_URL}/search?${params}`;
}

const RANGES = [
  { label: '15 минут', seconds: 15 * 60 },
  { label: 'Час', seconds: 60 * 60 },
  { label: 'Сутки', seconds: 24 * 60 * 60 },
];

const useStyles = makeStyles(theme => ({
  buttons: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' },
  query: {
    marginTop: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: 12,
    wordBreak: 'break-all',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Логи сервиса в Graylog. Запрос и стримы задаются аннотациями `graylog/query` (Lucene) и
 * `graylog/streams` (id стримов через запятую) в catalog-info.yaml.
 */
export const GraylogCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const a = entity.metadata.annotations ?? {};
  const query = a[QUERY_ANNOTATION];
  const streams = a[STREAMS_ANNOTATION];
  if (!query) return null;
  return (
    <InfoCard title="Логи в Graylog">
      <Box className={classes.buttons}>
        {RANGES.map(r => (
          <Button
            key={r.seconds}
            variant="outlined"
            color="primary"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            href={graylogSearchUrl(query, streams, r.seconds)}
            target="_blank"
            rel="noopener"
          >
            {r.label}
          </Button>
        ))}
      </Box>
      {query.trim() !== '*' && (
        <Typography className={classes.query}>{query}</Typography>
      )}
    </InfoCard>
  );
};
